import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../../components/layout/AppLayout';
import { PageHeader } from '../../components/common/PageHeader';
import api from '../../lib/api';
import { toast } from 'sonner';
import { LANGUAGES, SPECIALIZATIONS } from '../../lib/constants';
import { Calendar, CheckCircle2 } from 'lucide-react';

const CustomerBooking = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    source_language: 'Deutsch', target_language: '',
    specialization: '', service_type: 'video',
    date: '', time: '', duration: 60, min_duration: 60, location: '', notes: '',
    pricing_model: 'privat',
  });
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState(null);
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  React.useEffect(() => {
    api.get('/customers/me').then(r => {
      setCustomer(r.data);
      const enabled = !!r.data?.behoerden_billing_enabled;
      const ct = (r.data?.customer_type || 'privat').toLowerCase();
      // Default to behoerde only when both: customer_type is behoerde/justiz AND admin freigeschaltet
      if (enabled && (ct === 'behoerde' || ct === 'justiz')) {
        setForm(f => ({ ...f, pricing_model: 'behoerde' }));
      } else {
        // Make sure we never carry an illegal "behoerde" choice into the form
        setForm(f => ({ ...f, pricing_model: 'privat' }));
      }
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/jobs', form);
      const jobId = res.data.id;
      // Determine if upfront payment is required (private customers without
      // invoice-mode get redirected to Stripe Checkout)
      try {
        const req = await api.get(`/payments/required/${jobId}`);
        if (req.data?.required) {
          toast.message('Sie werden zur sicheren Bezahlung weitergeleitet…');
          const checkout = await api.post('/payments/checkout/session', { job_id: jobId });
          if (checkout.data?.url) {
            window.location.href = checkout.data.url;
            return;
          }
        }
      } catch (payErr) {
        // If payment lookup fails (e.g. Stripe not configured), still complete booking
        if (process.env.NODE_ENV === 'development') {
          console.warn('Payment step skipped:', payErr);
        }
      }
      toast.success('Termin angefragt! Wir bearbeiten Ihre Anfrage.');
      navigate('/customer/jobs');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler');
    } finally { setLoading(false); }
  };

  return (
    <AppLayout>
      <PageHeader title="Neuen Termin buchen" description="Fordern Sie einen Dolmetschtermin an. Unser Team bearbeitet Ihre Anfrage." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md card-subtle p-6 space-y-4" data-testid="booking-form">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Ausgangssprache *</label>
                <select required value={form.source_language} onChange={(e) => upd('source_language', e.target.value)} data-testid="booking-source-lang" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none">
                  {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Zielsprache *</label>
                <select required value={form.target_language} onChange={(e) => upd('target_language', e.target.value)} data-testid="booking-target-lang" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none">
                  <option value="">Bitte wählen...</option>
                  {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Fachgebiet</label>
                <select value={form.specialization} onChange={(e) => upd('specialization', e.target.value)} data-testid="booking-specialization" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none">
                  <option value="">Allgemein</option>
                  {SPECIALIZATIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Einsatzart *</label>
                <div className="flex gap-2">
                  {[['video', 'Video'], ['telefon', 'Telefon'], ['vor_ort', 'Vor-Ort']].map(([v, l]) => (
                    <button
                      key={v} type="button"
                      onClick={() => upd('service_type', v)}
                      data-testid={`booking-service-${v}`}
                      className={`flex-1 py-2 text-sm font-medium rounded-md border transition-colors ${form.service_type === v ? 'bg-[#0F4C81] text-white border-[#0F4C81]' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                    >{l}</button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Datum *</label>
                <input required type="date" min={new Date().toISOString().split('T')[0]} value={form.date} onChange={(e) => upd('date', e.target.value)} data-testid="booking-date" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Uhrzeit *</label>
                <input required type="time" value={form.time} onChange={(e) => upd('time', e.target.value)} data-testid="booking-time" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Dauer (Min.)</label>
                <input type="number" min="15" step="15" value={form.duration} onChange={(e) => { const v = parseInt(e.target.value) || 60; upd('duration', v); if (form.min_duration === form.duration) upd('min_duration', v); }} data-testid="booking-duration" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
                Mindestdauer für Abrechnung (Min.)
                <span className="ml-2 text-slate-400 normal-case font-normal">— wird bei kürzerem Meeting trotzdem berechnet</span>
              </label>
              <input type="number" min="15" step="15" value={form.min_duration} onChange={(e) => upd('min_duration', parseInt(e.target.value) || 60)} data-testid="booking-min-duration" className="w-full md:w-64 border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
            </div>

            {customer?.behoerden_billing_enabled && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
                Abrechnungsmodell
                <span className="ml-2 text-slate-400 normal-case font-normal">— wirkt sich auf Rechnungslogik aus</span>
              </label>
              <div className="flex gap-2" data-testid="booking-pricing-model">
                {[
                  ['privat', 'Privattarif', 'Aufrundung auf volle Stunde'],
                  ['behoerde', 'Behörden / Justiz', '15-Min-Pauschale + minutengenau'],
                ].map(([v, l, sub]) => (
                  <button
                    key={v} type="button"
                    onClick={() => upd('pricing_model', v)}
                    data-testid={`booking-pricing-${v}`}
                    className={`flex-1 text-left p-3 rounded-md border transition-colors ${form.pricing_model === v ? 'bg-[#0F4C81] text-white border-[#0F4C81]' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}
                  >
                    <div className="text-sm font-medium">{l}</div>
                    <div className={`text-xs mt-0.5 ${form.pricing_model === v ? 'text-white/80' : 'text-slate-500'}`}>{sub}</div>
                  </button>
                ))}
              </div>
            </div>
            )}

            {form.service_type === 'vor_ort' && (
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Einsatzort *</label>
                <input required={form.service_type === 'vor_ort'} value={form.location} onChange={(e) => upd('location', e.target.value)} data-testid="booking-location" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
            )}

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Bemerkungen</label>
              <textarea rows="3" value={form.notes} onChange={(e) => upd('notes', e.target.value)} data-testid="booking-notes" placeholder="Hintergrund zum Termin, wichtige Begriffe, Besonderheiten..." className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
            </div>

            <button type="submit" disabled={loading} data-testid="booking-submit-btn" className="w-full bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-3 text-sm transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
              <Calendar className="w-4 h-4" /> {loading ? 'Anfrage wird gesendet...' : 'Termin anfragen'}
            </button>
          </form>
        </div>

        <aside className="space-y-4">
          <div className="bg-[#0F4C81]/5 border border-[#0F4C81]/20 rounded-md p-5">
            <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3">Was passiert danach?</h3>
            <ol className="space-y-2 text-xs text-slate-700">
              {['Wir prüfen Ihre Anfrage', 'Ein passender Dolmetscher wird angefragt', 'Sie erhalten eine Bestätigung', 'Der Meetinglink wird Ihnen zugesendet'].map((t, i) => (
                <li key={i} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-[#0F4C81] flex-shrink-0 mt-0.5" /><span>{t}</span></li>
              ))}
            </ol>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <h3 className="font-heading text-sm font-semibold text-slate-900 mb-2">Standard-Preis</h3>
            <div className="font-mono text-2xl font-semibold text-[#0F4C81]">93,00 €</div>
            <div className="text-xs text-slate-500 mt-0.5">netto</div>
            <div className="mt-2 pt-2 border-t border-slate-100">
              <div className="font-mono text-sm text-slate-700">+ 19% MwSt. = <span className="font-semibold">110,67 €</span></div>
              <div className="text-xs text-slate-500 mt-1">pro angefangene Stunde</div>
            </div>
          </div>
        </aside>
      </div>
    </AppLayout>
  );
};

export default CustomerBooking;
